import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import VoiceSelection from '../../components/voiceSelection/VoiceSelection';
import { useUser } from '../../context/UserContext';
import './CreateStoryPage.css';

const CreateStoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const [theme, setTheme] = useState<string>('');
  const [vocab, setVocab] = useState<number>(50);
  const [difficulty, setDifficulty] = useState<string>('beginner');
  const [style, setStyle] = useState<string>('narrative');
  const [length, setLength] = useState<string>('medium');
  const [voiceId, setVoiceId] = useState<string>('random');
  const [languageCode] = useState<string>(
    userData?.learning_language_code || ''
  );
  const [vocabSizeData, setVocabSizeData] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchVocabSizeData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/vocabulary/size/${languageCode}`);
      setVocabSizeData(response.data);
    } catch (error) {
      console.error('Error fetching vocabulary size data:', error);
    }
  }, [languageCode]);

  const handleCreateStory = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const payload = {
        theme: theme,
        n_vocab: vocab,
        difficulty: difficulty,
        style: style,
        voice_id: voiceId,
        length: length,
      };
      await axios.post('/api/stories', payload);
      navigate('/stories');
      toast.success('Story creation started!');
    } catch (error: any) {
      console.error('Error creating story:', error);
      toast.error(`Error: ${error.response.data.detail}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVocabSizeData();
  }, [fetchVocabSizeData]);

  return (
    <div className="phn-page-container">
      <h1>Create New Story</h1>

      <div className="create-story-form-container">
        <div className="create-story-form">
          <div className="create-story-section">
            <div className="translate-input-wrapper">
              <input
                className="translate-input"
                type="text"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="Enter a theme for your story (optional)..."
              />
            </div>
          </div>

          <div className="create-story-section">
            <div className="create-story-label">
              <span>Vocabulary</span>
              {vocabSizeData < 50 && (
                <div className="vocab-warning-inline">
                  Need {50 - vocabSizeData} more words for "My Vocab"
                </div>
              )}
            </div>
            <div className="create-story-selections">
              <div
                className={`create-story-selections-item ${vocab === 0 ? 'active' : ''} ${
                  vocabSizeData < 50 ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (vocabSizeData >= 50) {
                    setVocab(0);
                  }
                }}
              >
                My Vocab
              </div>
              <div
                className={`create-story-selections-item ${vocab === 50 ? 'active' : ''}`}
                onClick={() => {
                  setVocab(50);
                }}
              >
                50 Words
              </div>
              <div
                className={`create-story-selections-item ${vocab === 100 ? 'active' : ''}`}
                onClick={() => {
                  setVocab(100);
                }}
              >
                100 Words
              </div>
              <div
                className={`create-story-selections-item ${vocab === 200 ? 'active' : ''}`}
                onClick={() => {
                  setVocab(200);
                }}
              >
                200 Words
              </div>
            </div>
          </div>

          <div className="create-story-section">
            <div className="create-story-label">
              <span>Difficulty</span>
            </div>
            <div className="create-story-selections">
              <div
                className={`create-story-selections-item ${difficulty === 'beginner' ? 'active' : ''}`}
                onClick={() => {
                  setDifficulty('beginner');
                }}
              >
                Beginner
              </div>
              <div
                className={`create-story-selections-item ${difficulty === 'intermediate' ? 'active' : ''}`}
                onClick={() => {
                  setDifficulty('intermediate');
                }}
              >
                Intermediate
              </div>
              <div
                className={`create-story-selections-item ${difficulty === 'advanced' ? 'active' : ''}`}
                onClick={() => {
                  setDifficulty('advanced');
                }}
              >
                Advanced
              </div>
            </div>
          </div>

          <div className="create-story-section">
            <div className="create-story-label">
              <span>Length</span>
            </div>
            <div className="create-story-selections">
              <div
                className={`create-story-selections-item ${length === 'short' ? 'active' : ''}`}
                onClick={() => {
                  setLength('short');
                }}
              >
                Short
              </div>
              <div
                className={`create-story-selections-item ${length === 'medium' ? 'active' : ''}`}
                onClick={() => {
                  setLength('medium');
                }}
              >
                Medium
              </div>
              <div
                className={`create-story-selections-item ${length === 'long' ? 'active' : ''}`}
                onClick={() => {
                  setLength('long');
                }}
              >
                Long
              </div>
            </div>
          </div>

          <div className="create-story-section">
            <div className="create-story-label">
              <span>Style</span>
            </div>
            <div className="create-story-selections">
              <div
                className={`create-story-selections-item ${style === 'narrative' ? 'active' : ''}`}
                onClick={() => {
                  setStyle('narrative');
                }}
              >
                Narrative
              </div>
              <div
                className={`create-story-selections-item ${style === 'conversation' ? 'active' : ''}`}
                onClick={() => {
                  setStyle('conversation');
                }}
              >
                Conversation
              </div>
              <div
                className={`create-story-selections-item ${style === 'first-person' ? 'active' : ''}`}
                onClick={() => {
                  setStyle('first-person');
                }}
              >
                First Person
              </div>
            </div>
          </div>

          <div className="create-story-section">
            <div className="create-story-label">
              <span>Voice</span>
            </div>
            <VoiceSelection
              languageCode={languageCode}
              text="Sample text for voice preview"
              onVoiceChange={(voice: string) => setVoiceId(voice)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="create-story-actions">
          <button
            className="phn-button create-story-create-btn"
            onClick={handleCreateStory}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Story'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateStoryPage;
